import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { ArrowLeft, Clock, Copy, Lock, Mail, Paperclip, UserRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtDateFull, fmtDuration, useNow } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusChip, Tag, tagTone } from "@/components/common/StatusChip";
import { LiveDuration, UserAvatar } from "@/components/common/Bits";
import TicketActions from "./TicketActions";
import { AttachmentList } from "./Attachments";
import Composer from "./Composer";
import Timeline from "./Timeline";

function TimingPanel({ t }) {
  const now = useNow();
  const open = t.status !== "closed";
  const created = new Date(t.created_at).getTime();
  const unworked = t.first_worked_at ? (new Date(t.first_worked_at).getTime() - created) / 1000 : (open ? (now - created) / 1000 : t.unworked_seconds);
  const work = t.active_session ? t.work_sessions.reduce((a, s) => a + (s.seconds || 0), 0) + (now - new Date(t.active_session.started_at).getTime()) / 1000 : t.work_seconds;
  const cells = [
    { label: "Opened", value: fmtDate(t.created_at), sub: t.source, testId: "timing-opened" },
    { label: t.first_worked_at ? "Sat unworked" : "Unworked for", value: fmtDuration(unworked), tone: !t.first_worked_at && open ? "text-amber-500" : "", sub: t.first_worked_at ? `first worked ${fmtDate(t.first_worked_at)}` : "not yet worked", testId: "timing-unworked" },
    { label: "Active work time", value: fmtDuration(work), tone: t.active_session ? "text-emerald-500" : "", sub: `${t.work_sessions.length + (t.active_session ? 1 : 0)} session(s)`, testId: "timing-work" },
    { label: open ? "Open for" : "Open → closed", value: fmtDuration(open ? (now - created) / 1000 : t.resolution_seconds), sub: t.closed_at ? `closed ${fmtDate(t.closed_at)}` : "still open", testId: "timing-resolution" },
  ];
  return (
    <div className="grid grid-cols-2 gap-px border border-border bg-border md:grid-cols-4" data-testid="timing-panel">
      {cells.map((c) => (
        <div key={c.label} className="bg-card px-3 py-2.5" data-testid={c.testId}>
          <div className="eyebrow">{c.label}</div>
          <div className={`mono mt-0.5 text-base font-semibold tabular-nums ${c.tone || ""}`}>{c.value}</div>
          <div className="truncate text-[11px] text-muted-foreground">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function LockedContent({ t }) {
  return (
    <div className="surface relative overflow-hidden p-4" data-testid="locked-content">
      <div className="select-none space-y-2 blur-sm" aria-hidden="true">
        <div className="h-3 w-11/12 bg-muted-foreground/30" /><div className="h-3 w-10/12 bg-muted-foreground/30" /><div className="h-3 w-3/4 bg-muted-foreground/30" /><div className="h-3 w-5/6 bg-muted-foreground/30" /><div className="h-3 w-1/2 bg-muted-foreground/30" />
        <div className="mt-3 flex gap-2"><div className="h-9 w-40 bg-muted-foreground/20" /><div className="h-9 w-32 bg-muted-foreground/20" /></div>
      </div>
      <div className="absolute inset-0 grid place-items-center bg-background/40 backdrop-blur-[2px]">
        <div className="flex max-w-sm flex-col items-center gap-1.5 border border-border bg-card px-5 py-4 text-center shadow-lg">
          <Lock className="h-5 w-5 text-muted-foreground" />
          <div className="text-sm font-semibold">Email body and {t.attachment_count || 0} attachment(s) are hidden</div>
          <div className="text-xs text-muted-foreground">This channel tracks open → close time. Click <strong>Start working</strong> above to reveal the content — the clock starts then. Pausing locks it again.</div>
        </div>
      </div>
    </div>
  );
}

function Message({ m, attachments, innerRef, latest, fresh }) {
  const kindStyle = { inbound: "border-cyan-500/50", reply: "border-blue-500/50", note: "border-amber-500/50 bg-amber-500/5" }[m.kind] || "";
  const label = { inbound: "Inbound email", reply: m.auto ? "Auto-reply sent" : "Reply sent", note: "Internal note" }[m.kind];
  const files = attachments.filter((a) => (m.attachment_ids || []).includes(a.id));
  return (
    <article ref={innerRef} id={`msg-${m.id}`} className={`border-l-2 ${kindStyle} surface scroll-mt-3 p-3 transition-shadow duration-700 ${fresh ? "ring-2 ring-primary/70 ring-offset-2 ring-offset-background" : ""}`}
      data-testid={`message-${m.kind}`} data-latest={latest || undefined} data-fresh={fresh || undefined}>
      <header className="flex flex-wrap items-center gap-2 text-xs">
        <UserAvatar name={m.author?.name || "?"} size="xs" />
        <span className="font-semibold">{m.author?.name}</span>
        {m.author?.email && <span className="mono text-muted-foreground">{m.author.email}</span>}
        {fresh && <span className="eyebrow text-primary" data-testid="message-new-badge">New</span>}
        <span className="eyebrow ml-auto">{label} · <span className="normal-case tracking-normal">{fmtDateFull(m.created_at)}</span></span>
      </header>
      {m.body_html ? <div className="prose prose-sm mt-2 max-w-none break-words text-sm [overflow-wrap:anywhere] dark:prose-invert" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body_html, { FORBID_TAGS: ["style", "form", "input", "iframe", "object", "embed"], ADD_ATTR: ["target"] }) }} /> : <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed [overflow-wrap:anywhere]">{m.body_text}</p>}
      {files.length > 0 && <AttachmentList files={files} className="mt-3" />}
    </article>
  );
}

/** Newest-first thread: the latest email is always at the top, so opening a ticket needs no scrolling. When a new email
 * arrives while the ticket is open we jump to it; a new note only raises a toast (and highlights it in the Notes tab). */
function useFollowLatest(t, id, tab, setTab, userId, onNewNote) {
  const topRef = useRef(null);
  const seen = useRef({ id: null, email: null, note: null, pending: false });
  const [fresh, setFresh] = useState(null);
  useEffect(() => {
    if (!t || t.locked) return;
    const emails = t.messages.filter((m) => m.kind !== "note");
    const notes = t.messages.filter((m) => m.kind === "note");
    const lastEmail = emails[emails.length - 1]?.id || null;
    const lastNote = notes[notes.length - 1]?.id || null;
    if (seen.current.id !== id) {
      seen.current = { id, email: lastEmail, note: lastNote, pending: false };
      return;
    }
    if (lastEmail && lastEmail !== seen.current.email) {
      const m = emails[emails.length - 1];
      seen.current.email = lastEmail;
      seen.current.pending = true;
      setFresh(lastEmail);
      if (tab !== "thread") setTab("thread");
      if (m.author?.id !== userId) toast.info(`New ${m.kind === "inbound" ? "email" : "reply"} on ${t.number}`, { description: `${m.author?.name || "Requester"} · ${(m.body_text || "").slice(0, 80)}` });
    }
    if (lastNote && lastNote !== seen.current.note) {
      const m = notes[notes.length - 1];
      seen.current.note = lastNote;
      setFresh(lastNote);
      if (m.author?.id !== userId) {
        onNewNote?.();
        toast.info(`New internal note on ${t.number}`, { description: `${m.author?.name} · ${(m.body_text || "").slice(0, 80)}`, action: { label: "Open notes", onClick: () => setTab("notes") } });
      }
    }
    if (seen.current.pending && tab === "thread" && topRef.current) {
      seen.current.pending = false;
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [t, id, tab, setTab, userId, onNewNote]);
  useEffect(() => {
    if (!fresh) return undefined;
    const timer = setTimeout(() => setFresh(null), 6000);
    return () => clearTimeout(timer);
  }, [fresh]);
  return { topRef, fresh };
}

export default function TicketDetail({ id, onBack }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState("thread");
  const [notesViewed, setNotesViewed] = useState(false);
  const { data: t, isLoading, error } = useQuery({ queryKey: ["ticket", id], queryFn: () => api.get(`/tickets/${id}`).then((r) => r.data), refetchInterval: 5000 });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ticket", id] }); qc.invalidateQueries({ queryKey: ["tickets"] }); qc.invalidateQueries({ queryKey: ["overview"] }); };
  const { topRef, fresh } = useFollowLatest(t, id, tab, setTab, user.id, useCallback(() => setNotesViewed(false), []));
  useEffect(() => { setTab("thread"); setNotesViewed(false); }, [id]);
  useEffect(() => {
    if (tab !== "notes" || !t || t.locked) return;
    setNotesViewed(true);
    api.post(`/tickets/${id}/notes-viewed`).catch(() => {});  // server-side record the approval gate checks
  }, [tab, id, t?.locked, t?.messages?.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <div className="p-8 text-xs text-muted-foreground mono" data-testid="ticket-loading">LOADING TICKET…</div>;
  if (error || !t) return <div className="p-8 text-sm text-red-500" data-testid="ticket-error">{errMsg(error, "Ticket not found")}</div>;

  const mine = t.active_session?.user_id === user.id;
  const emails = t.messages.filter((m) => m.kind !== "note").slice().reverse();
  const notes = t.messages.filter((m) => m.kind === "note").slice().reverse();
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="ticket-detail">
      <div className="border-b border-border bg-card px-4 py-3 md:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" className="-ml-2 h-7 px-2 text-xs" onClick={onBack} data-testid="ticket-back-btn"><ArrowLeft className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">All tickets</span></Button>
          <button onClick={() => navigator.clipboard?.writeText(t.number).then(() => toast.success(`${t.number} copied`))} className="mono inline-flex items-center gap-1 border border-border bg-muted/40 px-1.5 py-0.5 text-xs font-semibold transition-colors hover:bg-accent" title="Copy ticket number" data-testid="ticket-number">
            {t.number} <Copy className="h-3 w-3 text-muted-foreground" />
          </button>
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><span className="h-2 w-2" style={{ background: t.channel_color }} />{t.channel_name} <span className="mono">· {t.channel_email || t.to_email}</span></span>
          <StatusChip status={t.status} data-testid="ticket-status-chip" />
          {t.tags.map((tag) => <Tag key={tag} tone={tagTone(tag)} data-testid={`ticket-tag-${tag}`}>{tag}</Tag>)}
          {t.active_session && (
            <span className="ml-auto flex items-center gap-1.5 border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-300" data-testid="active-session-indicator">
              <span className="live-dot" /> {mine ? "You are" : `${t.active_session.user_name} is`} working · <LiveDuration from={t.active_session.started_at} clock />
            </span>
          )}
        </div>
        <h1 className="mt-2 text-lg font-bold leading-snug tracking-tight sm:text-xl" data-testid="ticket-subject">{t.subject}</h1>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {t.from_name ? `${t.from_name} <${t.from_email}>` : t.from_email}</span>
          <span className="flex items-center gap-1"><UserRound className="h-3.5 w-3.5" /> {t.assignee_name || "Unassigned"}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDateFull(t.created_at)}</span>
          {t.locked && <span className="flex items-center gap-1 text-amber-500" data-testid="ticket-locked-badge"><Lock className="h-3.5 w-3.5" /> {t.first_worked_at ? "paused — content locked" : "content locked until worked"}</span>}
          {!t.locked && t.attachments.length > 0 && <span className="flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> {t.attachments.length} attachment(s)</span>}
        </div>
        <TicketActions t={t} onChanged={refresh} noteCount={notes.length} notesViewed={notesViewed} onViewNotes={() => setTab("notes")} />
      </div>
      {t.status === "pending_approval" && t.approval && (
        <div className="flex flex-wrap items-center gap-2 border-b border-purple-500/40 bg-purple-500/10 px-4 py-2 text-xs md:px-6" data-testid="approval-banner">
          <strong>Closure awaiting approval.</strong> Requested by {t.approval.requested_by_name} {fmtDate(t.approval.requested_at)}.
          {t.approval.reason && <span className="text-muted-foreground">“{t.approval.reason}”</span>}
          {t.can_approve && <span className="ml-auto text-purple-600 dark:text-purple-300">You can approve or reject this request.</span>}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-6xl space-y-4 p-4 md:p-6">
          <TimingPanel t={t} />
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-8 rounded-none bg-muted/60 p-0.5">
              <TabsTrigger value="thread" className="h-7 rounded-none text-xs" data-testid="tab-thread">Thread ({emails.length})</TabsTrigger>
              <TabsTrigger value="notes" className="h-7 rounded-none text-xs" data-testid="tab-notes">Notes ({notes.length}){notes.length > 0 && t.status === "pending_approval" && t.can_approve && !notesViewed && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-amber-500" data-testid="notes-unread-dot" />}</TabsTrigger>
              <TabsTrigger value="attachments" className="h-7 rounded-none text-xs" data-testid="tab-attachments">Attachments ({t.locked ? t.attachment_count || 0 : t.attachments.length})</TabsTrigger>
              <TabsTrigger value="history" className="h-7 rounded-none text-xs" data-testid="tab-history">History ({t.events.length})</TabsTrigger>
            </TabsList>
          </Tabs>
          {tab === "thread" && (
            <div className="space-y-3" data-testid="ticket-thread">
              {t.locked ? <LockedContent t={t} /> : (
                <>
                  <Composer t={t} onChanged={refresh} mode="reply" />
                  {emails.map((m, i) => <Message key={m.id} m={m} attachments={t.attachments} innerRef={i === 0 ? topRef : undefined} latest={i === 0} fresh={fresh === m.id} />)}
                </>
              )}
            </div>
          )}
          {tab === "notes" && (
            <div className="space-y-3" data-testid="ticket-notes">
              {t.locked ? <LockedContent t={t} /> : (
                <>
                  <Composer t={t} onChanged={refresh} mode="note" />
                  {notes.length === 0 && <p className="text-xs text-muted-foreground" data-testid="no-notes">No internal notes yet.</p>}
                  {notes.map((m, i) => <Message key={m.id} m={m} attachments={t.attachments} latest={i === 0} fresh={fresh === m.id} />)}
                </>
              )}
            </div>
          )}
          {tab === "attachments" && (t.locked ? <LockedContent t={t} /> : t.attachments.length ? <AttachmentList files={t.attachments} large /> : <p className="text-xs text-muted-foreground" data-testid="no-attachments">No attachments on this ticket.</p>)}
          {tab === "history" && <Timeline events={t.events} />}
        </div>
      </div>
    </div>
  );
}
